import React, { FC } from 'react';
import classNames from 'classnames';

import { CheckItemButton } from 'Src/feature/check-item/CheckItemButton';

import { CheckItemButtonPropsWithUID, parseSeatNumber, Seat, SeatUID, Table, TableUID, useSeatAssignment } from './useSeatAssignment';

import styles from './HotKeyCheckItemButtons.module.scss';

const sectionId = 'HotKeyCheckItemButtons';

type CheckItemButtonGroup = { name: string; selected: boolean; checkItemButtonProps: CheckItemButtonPropsWithUID[] };

interface HotKeyCheckItemButtonsProps {
  // cancel out any current selections styles when on a payment route
  isPaymentRoute: boolean;
  checkItemButtonProps: CheckItemButtonPropsWithUID[];
}

const HotKeyCheckItemButtons: FC<HotKeyCheckItemButtonsProps> = ({
  isPaymentRoute,
  checkItemButtonProps,
}) => {
  const seatAssignment = useSeatAssignment();

  const groupMap = new Map<TableUID | SeatUID, CheckItemButtonGroup>();
  checkItemButtonProps.forEach((checkItemButtonProp) => {
    // group by single seat name or default to a table(shared group)
    let model: Table | Seat = seatAssignment.table;

    if(checkItemButtonProp.seatNames && checkItemButtonProp.seatNames.length === 1) {
      const seatName = checkItemButtonProp.seatNames[0];
      // translate current seat name label to an uid to bind client side seat names to server side seat names
      const seatModel = seatAssignment.seats.list.find((model) => model.shortName.toLowerCase() === seatName.toLowerCase());
      if (seatModel) model = seatModel;
    }

    // selected state of a group
    let isSelected = false;
    if (model.uid === seatAssignment.table.uid) {
      // current group is table/shared group
      if (seatAssignment.selections.current.length === 1 && seatAssignment.selections.current[0] === seatAssignment.table.uid) {
        // only table is selected
        isSelected = true;
      } else if (seatAssignment.selections.current.length > 1) {
        // multiple selections exist
        isSelected = true;
      }
    } else if(seatAssignment.selections.current.length === 1 && seatAssignment.selections.current[0] === model.uid) {
      // current group is a seat group
      // single selection matching current groupKey
      isSelected = true;
    }

    const groupItem = groupMap.get(model.uid) || { name: model.name, selected: isSelected, checkItemButtonProps: []  };
    groupItem.checkItemButtonProps.push(checkItemButtonProp);
    groupMap.set(model.uid, groupItem);
  });

  // attempt to add a fake group for ui
  // - only add when not on a payment route
  // - if section is selected => then show section header
  // - if section is not selected AND section is empty => then hide section header
  if (!isPaymentRoute && seatAssignment.selections.current.length) {
    // default to table/shared model
    let model: Table | Seat = seatAssignment.table;
    // attempt to find a model based on a single selection
    // - if more selections exist then the model should be the table/shared model
    if (seatAssignment.selections.current.length === 1) {
      const [singleSelectionUID] = seatAssignment.selections.current;
      // find a matching model based on the current single selection
      const seatModel = seatAssignment.seats.list.find(({ uid }) => uid === singleSelectionUID);
      // only set if found bc otherwise, we can presume this is for the table
      if (seatModel) model = seatModel;
    }
    const group = groupMap.get(model.uid);
    // group not found so we can presume that no selections exist for the currently selected group
    // - in this case we want to make a fake group to show a group title
    if (!group) {
      groupMap.set(model.uid, { name: model.name, selected: true, checkItemButtonProps: []  });
    }
  }

  // convert map into a list of groups and sort our groups
  const checkItemButtonPropsGrouped = [...groupMap.values()].sort((a, b) => {
    // when table, move to the front of the list
    if (a.name === seatAssignment.table.name) return -1;
    return parseSeatNumber(a.name) - parseSeatNumber(b.name);
  });

  return checkItemButtonPropsGrouped.map((group) => (
    <div key={group.name} className={classNames(styles.contentGroup, { [styles.contentGroupSelected]: group.selected && !isPaymentRoute })}>
      <span className={styles.contentGroupTitle}>{group.name}</span>
      {
        group.checkItemButtonProps.map(({ uuid, ...props }) => (
          <CheckItemButton key={uuid} uuid={uuid} {...props} className={styles.contentGroupBtn} />
        ))
      }
    </div>
  ));
};

export { HotKeyCheckItemButtons, sectionId };
export type { HotKeyCheckItemButtonsProps };
